import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FluentCard } from '@/components/layout/page-header'
import type { HeatmapData } from '@/lib/api/types'

interface UsageHeatmapProps {
  data: HeatmapData[]
  loading?: boolean
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const HOURS = Array.from({ length: 24 }, (_, i) => i)

export function UsageHeatmap({ data, loading }: UsageHeatmapProps) {
  // Build a map for quick lookup
  const heatmapMap = new Map<string, number>()
  data.forEach((d) => {
    heatmapMap.set(`${d.dayOfWeek}-${d.hour}`, d.count)
  })

  const maxCount = Math.max(...data.map((d) => d.count), 1)

  const getIntensity = (count: number) => {
    if (count === 0) return 0
    return Math.ceil((count / maxCount) * 4) // 1-4 intensity levels
  }

  const getColor = (intensity: number) => {
    switch (intensity) {
      case 0:
        return 'bg-gray-100 dark:bg-gray-800'
      case 1:
        return 'bg-blue-100 dark:bg-blue-900/30'
      case 2:
        return 'bg-blue-300 dark:bg-blue-700'
      case 3:
        return 'bg-blue-500 dark:bg-blue-500'
      case 4:
        return 'bg-blue-700 dark:bg-blue-400'
      default:
        return 'bg-gray-100 dark:bg-gray-800'
    }
  }

  if (loading) {
    return (
      <FluentCard>
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
            <Clock className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Heatmap</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Activity by day and hour</p>
          </div>
        </div>
        <div className="h-[200px] bg-gray-100 dark:bg-gray-800 rounded-xl animate-pulse" />
      </FluentCard>
    )
  }

  return (
    <FluentCard>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600">
            <Clock className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Usage Heatmap</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Activity by day and hour</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <span>Less</span>
          <div className="flex gap-0.5">
            {[0, 1, 2, 3, 4].map((intensity) => (
              <div
                key={intensity}
                className={cn('w-3 h-3 rounded-sm', getColor(intensity))}
              />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Hours header */}
          <div className="flex mb-1">
            <div className="w-10" />
            {HOURS.filter((h) => h % 3 === 0).map((hour) => (
              <div
                key={hour}
                className="flex-1 text-center text-xs text-gray-400"
              >
                {hour.toString().padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Heatmap grid */}
          <div className="space-y-1">
            {DAYS.map((day, dayIndex) => (
              <div key={day} className="flex items-center gap-1">
                <div className="w-10 text-xs text-gray-500 dark:text-gray-400 text-right pr-2">
                  {day}
                </div>
                <div className="flex-1 flex gap-0.5">
                  {HOURS.map((hour) => {
                    const count = heatmapMap.get(`${dayIndex}-${hour}`) ?? 0
                    const intensity = getIntensity(count)
                    return (
                      <div
                        key={hour}
                        className={cn(
                          'flex-1 aspect-square rounded-sm transition-all hover:ring-2 hover:ring-blue-400 hover:ring-offset-1 dark:hover:ring-offset-gray-900 cursor-default',
                          getColor(intensity)
                        )}
                        title={`${day} ${hour}:00 - ${count.toLocaleString()} events`}
                      />
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {data.length === 0 && (
        <div className="text-center py-8 mt-4">
          <Clock className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No heatmap data available</p>
        </div>
      )}
    </FluentCard>
  )
}
